import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { formatDecimal } from '../ui/numbers';
import { CATALOG_KEY, KIND_LABELS, type Kind } from './ItemForm';

/** An item as the duplicate finder shows it (only what the person may see). */
type DupItem = { id: string; name: string; kind?: Kind; ownedQuantity?: number; version: number };
type Preview = { into: DupItem; from: DupItem; effects: Array<{ key: string; label: string; count: number }>; filled: string[]; problem: string | null };

const DUPLICATES_KEY = [...CATALOG_KEY, 'duplicates'];

/** What merging `from` into `into` would do, and the "Połącz" itself (owner: a preview instead of "are you sure"). */
function MergePreview({ intoId, fromId, onClose }: { intoId: string; fromId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const preview = useQuery({
    queryKey: [...DUPLICATES_KEY, 'preview', intoId, fromId],
    queryFn: () => api<Preview>(`/api/catalog/items/${intoId}/merge-preview?from=${fromId}`),
  });
  const merge = useMutation({
    mutationFn: (p: Preview) => api(`/api/catalog/items/${intoId}/merge`, { method: 'POST', body: { fromId, version: p.into.version, fromVersion: p.from.version } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CATALOG_KEY });
      onClose();
    },
  });
  const p = preview.data;
  return (
    <section className="dup-preview" aria-label="Podgląd połączenia">
      {preview.isLoading && <p className="muted">Liczę, co się przeniesie…</p>}
      {preview.error && <p className="alert">{preview.error instanceof ApiError ? preview.error.message : 'Nie udało się przygotować podglądu'}</p>}
      {p && (
        <>
          <p>
            „{p.from.name}” zniknie z katalogu, a wszystko przejdzie do „{p.into.name}”:
          </p>
          {p.effects.length > 0 ? (
            <ul className="dup-effects">
              {p.effects.map((e) => (
                <li key={e.key}>
                  {e.label}: <strong>{e.count}</strong>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Nic poza samą pozycją.</p>
          )}
          {p.filled.length > 0 && <p className="muted">Uzupełnione z drugiej pozycji: {p.filled.join(', ')}</p>}
          {p.problem && <p className="alert">{p.problem}</p>}
          {merge.error && (
            <p className="alert" role="alert">
              {merge.error instanceof ApiError && merge.error.status === 409 ? 'Ktoś w międzyczasie zmienił jedną z pozycji. Otwórz podgląd jeszcze raz.' : merge.error instanceof ApiError ? merge.error.message : 'Nie udało się połączyć'}
            </p>
          )}
          <div className="actions">
            <button type="button" className="btn primary small" disabled={!!p.problem || merge.isPending} onClick={() => merge.mutate(p)}>
              Połącz
            </button>
            <button type="button" className="btn small" onClick={onClose}>
              Anuluj
            </button>
          </div>
        </>
      )}
    </section>
  );
}

/** One group of duplicates (or a possible pair): pick the main item, then merge the others into it one by one. */
function DuplicateGroup({ items, canMerge, onOpen }: { items: DupItem[]; canMerge: boolean; onOpen: (item: DupItem) => void }) {
  const [mainId, setMainId] = useState(items[0]!.id);
  const [mergingId, setMergingId] = useState<string | null>(null);
  const main = items.find((i) => i.id === mainId) ?? items[0]!;
  return (
    <div role="group" aria-label={`Duble: ${items[0]!.name}`} className="dup-group">
      <ul className="dup-items">
        {items.map((it) => {
          const detail = [it.kind ? KIND_LABELS[it.kind] : null, it.ownedQuantity !== undefined ? `${formatDecimal(it.ownedQuantity)} szt.` : null].filter(Boolean).join(' · ');
          return (
            <li key={it.id}>
              {canMerge ? (
                <label>
                  <input type="radio" name={`main-${items[0]!.id}`} checked={it.id === main.id} onChange={() => (setMainId(it.id), setMergingId(null))} />
                  <span>{it.name}</span>
                  {it.id === main.id && <span className="muted"> (główna)</span>}
                </label>
              ) : (
                <span>{it.name}</span>
              )}
              {detail && <span className="muted"> {detail}</span>}
              <button type="button" className="btn small ghost" onClick={() => onOpen(it)}>
                Otwórz
              </button>
            </li>
          );
        })}
      </ul>
      {canMerge &&
        items
          .filter((it) => it.id !== main.id)
          .map((it) => (
            <div key={it.id}>
              {mergingId === it.id ? (
                <MergePreview intoId={main.id} fromId={it.id} onClose={() => setMergingId(null)} />
              ) : (
                <button type="button" className="btn small" onClick={() => setMergingId(it.id)}>
                  Połącz „{it.name}” z „{main.name}”…
                </button>
              )}
            </div>
          ))}
    </div>
  );
}

/**
 * "Znajdź duble" under the catalog's ⚙ (P14): items with the same name (case, spaces, hyphens, Polish letters aside)
 * and, apart, "Możliwe duble" where one name holds the other. Nothing merges by itself: the owner picks the main item,
 * sees what moves and clicks "Połącz". Merging needs editing and deleting catalog items (`canMerge`).
 */
export function DuplicatesPanel({ canMerge, onOpen }: { canMerge: boolean; onOpen: (item: DupItem) => void }) {
  const list = useQuery({ queryKey: DUPLICATES_KEY, queryFn: () => api<{ groups: DupItem[][]; possible: DupItem[][] }>('/api/catalog/duplicates') });
  // a group whose items the person may not all see is shown with what is left (at least two)
  const groups = (list.data?.groups ?? []).filter((g) => g.length > 1);
  const possible = (list.data?.possible ?? []).filter((g) => g.length > 1);
  return (
    <section className="panel dup-panel" aria-label="Duble w katalogu" data-feature-id="catalog.duplicates">
      <h2>Duble w katalogu</h2>
      <p className="muted">Pozycje o tej samej nazwie (bez względu na wielkość liter, spacje, myślniki i polskie znaki). Liczby i oznaczenia modelu muszą się zgadzać.</p>
      {list.isLoading && <p className="muted">Szukam dubli…</p>}
      {list.error && <p className="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać dubli'}</p>}
      {list.data && groups.length === 0 && <p>Nie znaleziono dubli.</p>}
      {groups.map((g) => (
        <DuplicateGroup key={g.map((i) => i.id).join()} items={g} canMerge={canMerge} onOpen={onOpen} />
      ))}
      {possible.length > 0 && (
        <section aria-label="Możliwe duble" className="dup-possible">
          <h3>Możliwe duble</h3>
          <p className="muted">Jedna nazwa zawiera całą drugą (np. „Shure SM58” i „SM58”). Oceń sam — to mogą być różne rzeczy.</p>
          {possible.map((g) => (
            <DuplicateGroup key={g.map((i) => i.id).join()} items={g} canMerge={canMerge} onOpen={onOpen} />
          ))}
        </section>
      )}
    </section>
  );
}
